import React, { useEffect, useMemo } from "react";
import VStack from "../../ui_atoms/VStack";
import Switch from "../../ui_atoms/Switch";
import { StyleSheet } from "react-native";
import { EarningType, Mission, Repetition, Unit } from "../../__generated__/graphql";
import { useFormatWorkingTimeClassicSpec, useI18n } from "../../hooks/i18n-hooks";
import DateField from "../DateField";
import { Buildable } from "ts-essentials";
import HStack from "../../ui_atoms/HStack";
import Text from "../Text";
import { addDays, startOfYear } from "date-fns";
import { BodySmallText } from "../FormattedText";
import { useTheme } from "../../hooks/use-theme";

const styles = StyleSheet.create({
    items: {
        textAlign: "auto",
        alignSelf: "stretch",
    },
    section: {
        alignSelf: "stretch",
        flexDirection: "row",
        alignItems: "center",
    },
    text: {
        marginLeft: 10,
    },
});

type MissionDurationProps = {
    mission: Buildable<Mission>;
    isCdi: boolean;
    setIsCdi: (value: boolean) => void;
    setStartDate: (startDate: Date | null) => void;
    setEndDate: (endDate: Date | null) => void;
};

const MAX_HOURS_PER_DAY = 12;

const MissionDuration: React.FC<MissionDurationProps> = ({
    mission,
    isCdi,
    setIsCdi,
    setStartDate,
    setEndDate,
}) => {
    const i18n = useI18n();
    const { colors, fonts } = useTheme();
    const workingTime = mission.currentContract?.workingTimeClassicSpec;
    const missionEarningType = mission.currentContract?.earningType;

    const startDate = React.useMemo(() => {
        const startDate = mission.currentContract?.startDate;
        if (!startDate) {
            return undefined;
        }
        return new Date(startDate);
    }, [mission.currentContract?.startDate]);

    const endDate = React.useMemo(() => {
        const endDate = mission.currentContract?.endDate;
        if (!endDate) {
            return undefined;
        }
        return new Date(endDate);
    }, [mission.currentContract?.endDate]);

    const changeHasEndDate = React.useCallback(
        (value: boolean) => {
            setIsCdi(value);
            if (value) {
                setEndDate(null);
            }
        },
        [setEndDate, setIsCdi],
    );
    const minimumStartDate = React.useMemo(() => {
        // Allow backdating a contract as far as the first day of the current year
        return startOfYear(new Date());
    }, []);

    const minimumEndDate = React.useMemo(() => {
        if (mission.currentContract?.startDate) {
            let minDurationInDays = 1;
            if (
                mission.currentContract?.workingTimeClassicSpec?.repetition === Repetition.None &&
                mission.currentContract.workingTimeClassicSpec.unitCount
            ) {
                if (mission.currentContract?.workingTimeClassicSpec?.unit === Unit.Days) {
                    minDurationInDays = mission.currentContract.workingTimeClassicSpec.unitCount;
                } else if (mission.currentContract?.workingTimeClassicSpec?.unit === Unit.Hours) {
                    minDurationInDays = Math.ceil(
                        mission.currentContract.workingTimeClassicSpec.unitCount /
                            MAX_HOURS_PER_DAY,
                    );
                }
            } else if (mission.currentContract?.earningType === EarningType.Monthly) {
                // For monthly earning type, we set a minimum duration of 30 days
                minDurationInDays = 30;
            }
            const minEndDate = addDays(
                new Date(mission.currentContract.startDate),
                minDurationInDays - 1, // because end date is included
            );
            const minEndTime = Math.max(minimumStartDate.getTime(), minEndDate.getTime());
            return new Date(minEndTime);
        }
        return new Date(minimumStartDate.getTime());
    }, [
        minimumStartDate,
        mission.currentContract?.earningType,
        mission.currentContract?.startDate,
        mission.currentContract?.workingTimeClassicSpec,
    ]);

    const maximumEndDate = React.useMemo(() => {
        if (mission.currentContract?.startDate) {
            if (mission.currentContract?.workingTimeClassicSpec?.repetition === Repetition.None) {
                return addDays(new Date(mission.currentContract.startDate), 30);
            }
        }
        return undefined;
    }, [mission.currentContract?.startDate, mission.currentContract?.workingTimeClassicSpec]);

    const forceCdd = React.useMemo(() => {
        return mission.currentContract?.workingTimeClassicSpec?.repetition === Repetition.None;
    }, [mission.currentContract?.workingTimeClassicSpec?.repetition]);

    useEffect(() => {
        if (forceCdd) {
            setIsCdi(false);
        }
    }, [forceCdd, setIsCdi]);

    useEffect(() => {
        if (endDate && endDate < minimumEndDate) {
            setEndDate(minimumEndDate);
        }
    }, [minimumEndDate, endDate, setEndDate]);

    const endDateComponent = useMemo(
        () =>
            !isCdi || forceCdd ? (
                <DateField
                    testID="create-mission-end-date-input"
                    label={i18n.t("screens.user.create_mission_duration.end_date_label")}
                    setDateFn="EndDate"
                    date={startDate || addDays(new Date(), 5)}
                    value={endDate}
                    onChange={setEndDate}
                    minimumDate={minimumEndDate}
                    maximumDate={maximumEndDate}
                />
            ) : null,
        [endDate, i18n, isCdi, minimumEndDate, setEndDate, startDate, forceCdd, maximumEndDate],
    );

    const formatWorkingTime = useFormatWorkingTimeClassicSpec();

    return (
        <VStack gap={1.5} style={{ width: "100%" }}>
            <DateField
                testID="create-mission-start-date-input"
                setDateFn="StartDate"
                label={i18n.t("screens.user.create_mission_duration.start_date_label")}
                value={startDate}
                // Initial date
                date={addDays(new Date(), 5)}
                onChange={setStartDate}
                minimumDate={minimumStartDate}
            />
            {!forceCdd && (
                <HStack gap="1/2" style={styles.section}>
                    <Switch value={!isCdi} onChange={(v) => changeHasEndDate(!v)} />
                    <Text style={fonts.bodySmall} onPress={() => changeHasEndDate(!isCdi)}>
                        {i18n.t("screens.user.create_mission_duration.cdd_label")}
                    </Text>
                </HStack>
            )}
            {endDateComponent}
            {missionEarningType &&
                [EarningType.Daily, EarningType.Hourly].includes(missionEarningType) && (
                    <BodySmallText color={colors.fieldLabel}>
                        {i18n.t(
                            `screens.user.create_mission_duration.review_description_${missionEarningType}`,
                            {
                                workingTime: workingTime && formatWorkingTime(workingTime),
                                hours: i18n.t("utils.working_time.daily_hours", {
                                    count: workingTime?.dailyHours || 0,
                                }),
                            },
                        )}
                    </BodySmallText>
                )}
        </VStack>
    );
};

export default MissionDuration;
